import type { Auth0Client, IdToken } from '@auth0/auth0-spa-js';
import history from '@/settings/history';
import { INITIAL_URL } from '@/constants';
import { getAuth0Client, isTestEnv } from './client';
import { AuthStorage } from './storage';
import type { AuthProfile, AuthResultLike } from './types';

const hasAuthRedirectParams = (search: string): boolean => {
  return search.includes('code=') || search.includes('error=');
};

const secondsUntil = (exp?: number): number => {
  if (!exp) return 3600;
  const nowSec = Math.floor(Date.now() / 1000);
  return Math.max(0, exp - nowSec);
};

const idTokenToRaw = (idTokenClaims: IdToken | undefined): string => {
  // __raw is commonly present; type may not expose it as public
  const anyClaims = idTokenClaims as unknown as { __raw?: string };
  return anyClaims?.__raw ?? JSON.stringify(idTokenClaims ?? {});
};

const buildProfile = (payload: Record<string, unknown>): AuthProfile => {
  return {
    family_name: payload['family_name'] as string | undefined,
    given_name: payload['given_name'] as string | undefined,
    locale: payload['locale'] as string | undefined,
    name: payload['name'] as string | undefined,
    nickname: payload['nickname'] as string | undefined,
    nonce: payload['nonce'] as string | undefined,
    picture: payload['picture'] as string | undefined,
    sub: payload['sub'] as string | undefined,
    updated_at: payload['updated_at'] as string | undefined,
    email: payload['email'] as string | undefined,
    email_verified: payload['email_verified'] as boolean | undefined,
  };
};

export type GetAuth0ClientFn = () => Promise<Auth0Client>;

export class AuthService {
  private client: Auth0Client | null = null;
  private readonly getClientFn: GetAuth0ClientFn;

  constructor(getClientFn?: GetAuth0ClientFn) {
    this.getClientFn = getClientFn ?? getAuth0Client;
  }

  private async getClient(): Promise<Auth0Client> {
    if (!this.client) this.client = await this.getClientFn();
    return this.client;
  }

  async login(): Promise<void> {
    const client = await this.getClient();
    await client.loginWithRedirect();
  }

  async handleAuthentication(): Promise<void> {
    if (!hasAuthRedirectParams(window.location.search)) return;

    const client = await this.getClient();

    try {
      await client.handleRedirectCallback();
      window.history.replaceState({}, document.title, window.location.pathname);

      const accessToken = await client.getTokenSilently();
      const user = await client.getUser(); // may be undefined
      const idTokenClaims = await client.getIdTokenClaims();

      const payload: Record<string, unknown> =
        (user as unknown as Record<string, unknown> | undefined) ??
        (idTokenClaims as unknown as Record<string, unknown> | undefined) ??
        {};

      const authResult: AuthResultLike = {
        accessToken,
        idToken: idTokenToRaw(idTokenClaims),
        idTokenPayload: payload,
        expiresIn: secondsUntil(idTokenClaims?.exp),
      };

      AuthStorage.setSession({
        accessToken: authResult.accessToken,
        idToken: authResult.idToken,
        expiresAt: Date.now() + authResult.expiresIn * 1000,
      });

      AuthStorage.setProfile(buildProfile(authResult.idTokenPayload));

      const initial = localStorage.getItem(INITIAL_URL);
      if (initial) history.replace(initial);

      window.location.reload();
    } catch (err) {
      console.error('Authentication error:', err);
    }
  }

  async logout(): Promise<void> {
    const client = await this.getClient();

    AuthStorage.clearSession();
    AuthStorage.clearProfile();

    await client.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`,
      },
    });

    history.replace('/login');
    window.location.reload();
  }

  getProfile(): AuthProfile | null {
    return AuthStorage.getProfile();
  }

  isAuthenticated(): boolean {
    if (isTestEnv) return true;
    const expiresAt = AuthStorage.getExpiresAt();
    if (!expiresAt) return false;
    return Date.now() < expiresAt;
  }
}

export const auth = new AuthService();
